import React, { useId } from "react";

/**
 * Footer for `<DataTable/>`: rows-per-page, the visible range and the
 * first/prev/next/last pager. Works for both client-side and server-side
 * (manual) pagination — the table instance already hides that difference.
 *
 * `total` is the number of rows behind the whole table when it is known. When
 * it is not (server endpoints here return `total_pages` only) the range is
 * replaced by "Page x of y".
 */
const TablePagination = ({ table, pageSizeOptions, total }) => {
  const id = useId();
  const { pageIndex, pageSize } = table.state.pagination;
  const pageCount = table.getPageCount();
  const knownTotal = typeof total === "number" && Number.isFinite(total);
  const first = pageIndex * pageSize + 1;
  const last = knownTotal
    ? Math.min(first + pageSize - 1, total)
    : first + table.getRowModel().rows.length - 1;

  return (
    <div className="dt-footer">
      <div className="dt-footer-size">
        <label className="dt-footer-label" htmlFor={"dt-page-size" + id}>
          Rows per page
        </label>
        <select
          id={"dt-page-size" + id}
          className="form-select dt-page-size"
          value={pageSize}
          onChange={(event) => table.setPageSize(Number(event.target.value))}
        >
          {pageSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <p className="dt-footer-range" aria-live="polite">
        {knownTotal ? (
          <>
            <span className="dt-strong">
              {total === 0 ? 0 : first}-{Math.max(last, 0)}
            </span>{" "}
            of <span className="dt-strong">{total}</span>
          </>
        ) : (
          <>
            Page <span className="dt-strong">{pageIndex + 1}</span>
            {pageCount > 0 ? (
              <>
                {" "}
                of <span className="dt-strong">{pageCount}</span>
              </>
            ) : null}
          </>
        )}
      </p>

      <div className="dt-pager">
        <button
          type="button"
          className="dt-page-btn"
          aria-label="Go to first page"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.firstPage()}
        >
          <i className="fa fa-angle-double-left" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className="dt-page-btn"
          aria-label="Go to previous page"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.previousPage()}
        >
          <i className="fa fa-angle-left" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className="dt-page-btn"
          aria-label="Go to next page"
          disabled={!table.getCanNextPage()}
          onClick={() => table.nextPage()}
        >
          <i className="fa fa-angle-right" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          className="dt-page-btn"
          aria-label="Go to last page"
          disabled={!table.getCanNextPage() || pageCount < 1}
          onClick={() => table.lastPage()}
        >
          <i className="fa fa-angle-double-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  );
};

export default TablePagination;
